import { Suspense, lazy } from "react";
import { Route, Navigate, useLocation } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { permissionService } from "./services/permissionService";
import { renderSkinHairAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";
import FranchisePassportGate from "./components/FranchisePassportGate";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const IbDashboard = lazy(() => import("./pages/IbDashboard"));
const CheckIns = lazy(() => import("./pages/CheckIns"));
const PartnerDeskGuestRegister = lazy(() => import("./pages/PartnerDeskGuestRegister"));
const PassportGuestRegister = lazy(() => import("./pages/PassportGuestRegister"));
const PassportGuestEdit = lazy(() => import("./pages/PassportGuestEdit"));
const PartnerOperationsAppointments = lazy(() => import("./pages/PartnerOperationsAppointments"));
const PartnerCheckinAnalytics = lazy(() => import("./pages/PartnerCheckinAnalytics"));
const VisitServices = lazy(() => import("./pages/VisitServices"));
const Customers = lazy(() => import("./pages/Customers"));
const PartnerCustomerAnalytics = lazy(() => import("./pages/PartnerCustomerAnalytics"));
const PartnerFlowAnalytics = lazy(() => import("./pages/PartnerFlowAnalytics"));
const PartnerServiceAnalytics = lazy(() => import("./pages/PartnerServiceAnalytics"));
const PartnerStaffAnalytics = lazy(() => import("./pages/PartnerStaffAnalytics"));
const StaffReport = lazy(() => import("./pages/StaffReport"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const KioskLoginCredentials = lazy(() => import("./pages/KioskLoginCredentials"));
const AppsDownload = lazy(() => import("./pages/AppsDownload"));
const DashboardStaffUsers = lazy(() => import("./pages/DashboardStaffUsers"));
const ReelsManagement = lazy(() => import("./pages/ReelsManagement"));
const StoreLocation = lazy(() => import("./pages/StoreLocation"));
const PriceList = lazy(() => import("./pages/PriceList"));
const FranchiseProductList = lazy(() => import("./pages/FranchiseProductList"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Staff = lazy(() => import("./pages/Staff"));
const StaffAvailability = lazy(() => import("./pages/StaffAvailability"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Contact = lazy(() => import("./pages/Contact"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const OverviewSectionHubPage = lazy(() => import("../admin-shared/pages/OverviewSectionHubPage"));
const EngagementApp = lazy(() => import("../engagement/EngagementApp"));

const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/naturals/partner/overview" replace />;
  }
  return children;
};

function PartnerEngagementLegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/naturals\/partner\/demo-engagement\/?/, "");
  const target = rest
    ? `/naturals/partner/engagement/${rest}`
    : "/naturals/partner/engagement/";
  return <Navigate to={`${target}${location.search || ""}`} replace />;
}

export function renderPartnerRoutes({ partnerHome = false } = {}) {
  return (
    <>
      {partnerHome ? <Route path="/" element={<Navigate to="/naturals/partner" replace />} /> : null}
      <Route path="/naturals/partner" element={<Navigate to="/naturals/login" replace />} />
      <Route
        path="/naturals/partner/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="partner" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route
        path="/naturals/partner/overview/:sectionId"
        element={
          <Layout>
            {withSuspense(
              <OverviewSectionHubPage portal="partner" permissionService={permissionService} />,
            )}
          </Layout>
        }
      />
      <Route
        path="/naturals/partner/ib-dashboard"
        element={<Layout dashboardOnly>{withSuspense(<IbDashboard />)}</Layout>}
      />
      <Route
        path="/naturals/partner/operations/appointments"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerOperationsAppointments />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/guest/appointments"
        element={<Navigate to="/naturals/partner/operations/appointments" replace />}
      />
      <Route
        path="/naturals/partner/engagement/appointments"
        element={<Navigate to="/naturals/partner/operations/appointments" replace />}
      />
      <Route
        path="/naturals/partner/engagement/appointments/*"
        element={<Navigate to="/naturals/partner/operations/appointments" replace />}
      />
      <Route
        path="/naturals/partner/engagement/*"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/engagement/"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/engagement"
        element={<Navigate to="/naturals/partner/engagement/" replace />}
      />
      <Route path="/naturals/partner/demo-engagement/*" element={<PartnerEngagementLegacyRedirect />} />
      <Route
        path="/naturals/partner/demo-engagement"
        element={<Navigate to="/naturals/partner/engagement/" replace />}
      />
      <Route
        path="/naturals/partner/check-ins"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<CheckIns />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/desk-guest-register"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerDeskGuestRegister />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/passport-register/:checkinId"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <FranchisePassportGate>
              <Layout>{withSuspense(<PassportGuestRegister />)}</Layout>
            </FranchisePassportGate>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/passport-edit/:checkinId"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <FranchisePassportGate>
              <Layout>{withSuspense(<PassportGuestEdit />)}</Layout>
            </FranchisePassportGate>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/check-in-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerCheckinAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/visit-services"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<VisitServices />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/customers"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<Customers />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/customer-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessGuestOpsAnalytics()}>
            <Layout>{withSuspense(<PartnerCustomerAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/flow-analytics"
        element={
          <PermRoute check={() => permissionService.canAccessFlowAnalytics()}>
            <Layout>{withSuspense(<PartnerFlowAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/service-analytics"
        element={
          <PermRoute
            check={() =>
              permissionService.canAccessSkinAnalytics() ||
              permissionService.canAccessHairAnalytics()
            }
          >
            <Layout>{withSuspense(<PartnerServiceAnalytics />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/staff-report"
        element={<Layout>{withSuspense(<StaffReport />)}</Layout>}
      />
      <Route
        path="/naturals/partner/staff-analytics"
        element={<Navigate to="/naturals/partner/staff-report" replace />}
      />
      <Route
        path="/naturals/partner/customers/:faceUserId"
        element={<Layout>{withSuspense(<CustomerDetail />)}</Layout>}
      />
      <Route
        path="/naturals/partner/kiosk-login"
        element={
          <PermRoute check={() => permissionService.canAccessKioskSkinStack()}>
            <Layout>{withSuspense(<KioskLoginCredentials />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/guest-qr"
        element={<Navigate to="/naturals/partner/apps" replace />}
      />
      <Route
        path="/naturals/partner/apps"
        element={
          <PermRoute check={() => !permissionService.isDashboardStaff()}>
            <Layout>{withSuspense(<AppsDownload />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/dashboard-staff"
        element={
          <PermRoute check={() => permissionService.canManageDashboardStaff()}>
            <Layout>{withSuspense(<DashboardStaffUsers />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/reels"
        element={
          <PermRoute check={() => permissionService.canAccessReels()}>
            <Layout>{withSuspense(<ReelsManagement />)}</Layout>
          </PermRoute>
        }
      />
      <Route path="/naturals/partner/salon-app" element={<Navigate to="/naturals/partner/apps" replace />} />
      <Route path="/naturals/partner/reels-app" element={<Navigate to="/naturals/partner/apps" replace />} />
      <Route path="/naturals/partner/passport-app" element={<Navigate to="/naturals/partner/apps" replace />} />
      <Route
        path="/naturals/partner/store-location"
        element={<Layout>{withSuspense(<StoreLocation />)}</Layout>}
      />
      <Route
        path="/naturals/partner/price-list"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<PriceList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/product-list"
        element={
          <PermRoute check={() => permissionService.canAccessProductCatalogAdmin()}>
            <Layout>{withSuspense(<FranchiseProductList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/naturals/partner/my-orders"
        element={<Layout hideSidebar>{withSuspense(<MyOrders />)}</Layout>}
      />
      <Route
        path="/naturals/partner/productlist"
        element={<Navigate to="/naturals/partner/product-list" replace />}
      />
      <Route
        path="/naturals/partner/staff"
        element={<Layout>{withSuspense(<Staff />)}</Layout>}
      />
      <Route
        path="/naturals/partner/staff-availability"
        element={<Layout>{withSuspense(<StaffAvailability />)}</Layout>}
      />
      <Route
        path="/naturals/partner/naturals-kpis"
        element={<Layout>{withSuspense(<NaturalsKpis />)}</Layout>}
      />
      <Route path="/naturals/partner/complaints" element={<Layout>{withSuspense(<Complaints />)}</Layout>} />
      <Route path="/naturals/partner/edit-profile" element={<Layout>{withSuspense(<EditProfile />)}</Layout>} />
      <Route path="/naturals/partner/contact-us" element={<Layout>{withSuspense(<Contact />)}</Layout>} />
      {renderSkinHairAnalyticsRoutes({
        portalId: "partner",
        basePath: "/naturals/partner",
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
      })}
    </>
  );
}
